import React from "react";
import { PluginStore } from "graylog-web-plugin/plugin";
import isNumber from "lodash/isNumber";
import { Select, HoverForHelp } from "components/common";
import {
  Button,
  ButtonToolbar,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  HelpBlock,
  InputGroup,
  Input,
  Row
} from "components/bootstrap";
import EventKeyHelpPopover from "components/event-definitions/common/EventKeyHelpPopover";
import * as FormsUtils from "util/FormsUtils";
import withTelemetry from "logic/telemetry/withTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import commonStyles from "../common/commonStyles.css";
const requiredFields = ["fieldName"];
const getProviderPlugin = (type) => {
  if (type === void 0) {
    return void 0;
  }
  return PluginStore.exports("fieldValueProviders").find((edt) => edt.type === type);
};
const getConfigProviderType = (config, defaultValue) => {
  var _a, _b, _c;
  return (_c = (_b = (_a = config == null ? void 0 : config.providers) == null ? void 0 : _a[0]) == null ? void 0 : _b.type) != null ? _c : defaultValue;
};
const formatFieldValueProviders = () => PluginStore.exports("fieldValueProviders").map((type) => ({ label: type.displayName, value: type.type }));
class FieldForm extends React.Component {
  constructor(props) {
    super(props);
    this.validate = () => {
      var _a;
      const { isKey, keyPosition, config } = this.state;
      const errors = {};
      const providerType = getConfigProviderType(config);
      let pluginRequiredFields = [];
      if (providerType) {
        const providerPlugin = getProviderPlugin(providerType);
        pluginRequiredFields = providerPlugin == null ? void 0 : providerPlugin.requiredFields;
      }
      requiredFields.forEach((requiredField) => {
        if (!this.state[requiredField]) {
          errors[requiredField] = "Field cannot be empty.";
        }
      });
      if (!((_a = config.providers[0]) == null ? void 0 : _a.type)) {
        errors["config.providers[0].type"] = "Field cannot be empty.";
      }
      if (isKey && (!isNumber(keyPosition) || Number(keyPosition) < 1)) {
        errors.key_position = "Field must be a positive number.";
      }
      pluginRequiredFields.forEach((requiredField) => {
        var _a2, _b;
        if (!((_b = (_a2 = config == null ? void 0 : config.providers) == null ? void 0 : _a2[0]) == null ? void 0 : _b[requiredField])) {
          errors[requiredField] = "Field cannot be empty.";
        }
      });
      const errorNumber = Object.keys(errors).length;
      if (errorNumber > 0) {
        this.setState({ validation: { errors } });
      }
      return errorNumber === 0;
    };
    this.handleSubmit = () => {
      this.props.sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_FIELDS.DONE_CLICKED, {
        app_action_value: "done-button"
      });
      if (this.validate()) {
        const { fieldName: prevFieldName, onChange } = this.props;
        const { fieldName, config, isKey, keyPosition } = this.state;
        onChange(prevFieldName, fieldName, config, isKey, keyPosition - 1);
      }
    };
    this.handleCancel = () => {
      this.props.sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_FIELDS.CANCEL_CLICKED, {
        app_action_value: "cancel-button"
      });
      this.props.onCancel();
    };
    this.handleFieldNameChange = (event) => {
      const nextFieldName = FormsUtils.getValueFromInput(event.target);
      this.setState({ fieldName: nextFieldName });
    };
    this.handleConfigChange = (nextConfig) => {
      this.setState({ config: nextConfig });
    };
    this.handleProviderTypeChange = (nextProvider) => {
      this.props.sendTelemetry(
        nextProvider === "lookup-v1" ? TELEMETRY_EVENT_TYPE.EVENTDEFINITION_FIELDS.SET_VALUE_FROM_LOOKUP_TABLE_SELECTED : TELEMETRY_EVENT_TYPE.EVENTDEFINITION_FIELDS.SET_VALUE_FROM_TEMPLATE_SELECTED,
        {
          app_action_value: "set-value-from-select",
          value_source: nextProvider
        }
      );
      const { config } = this.state;
      const providerPlugin = getProviderPlugin(nextProvider);
      const defaultProviderConfig = providerPlugin.defaultConfig || {};
      const nextConfig = {
        ...config,
        providers: [
          {
            ...defaultProviderConfig,
            type: nextProvider
          }
        ]
      };
      this.handleConfigChange(nextConfig);
    };
    this.handleKeySortChange = (event) => {
      const nextPosition = event.target.value === "" ? "" : FormsUtils.getValueFromInput(event.target);
      this.setState({ keyPosition: nextPosition });
    };
    this.toggleKey = (event) => {
      const checked = FormsUtils.getValueFromInput(event.target);
      this.props.sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_FIELDS.AS_EVENT_KEY_TOGGLED, {
        app_action_value: "event-key-checkbox",
        event_key_checked: checked
      });
      this.setState({ isKey: checked });
    };
    this.renderFieldValueProviderForm = () => {
      const { fieldName, config, validation } = this.state;
      const { currentUser } = this.props;
      const providerType = getConfigProviderType(config);
      if (!providerType) {
        return null;
      }
      const providerPlugin = getProviderPlugin(providerType);
      return (providerPlugin == null ? void 0 : providerPlugin.formComponent) ? React.createElement(providerPlugin.formComponent, {
        fieldName,
        config,
        onChange: this.handleConfigChange,
        validation,
        currentUser
      }) : /* @__PURE__ */ React.createElement("div", null, "Selected provider is not available.");
    };
    const keyIndex = props.keys.indexOf(props.fieldName);
    this.state = {
      fieldName: props.fieldName,
      config: { data_type: "string", providers: [], ...props.config },
      isKey: keyIndex >= 0,
      keyPosition: keyIndex >= 0 ? keyIndex + 1 : props.keys.length + 1,
      validation: { errors: {} }
    };
  }
  render() {
    const { fieldName: prevFieldName } = this.props;
    const { fieldName, isKey, keyPosition, config, validation } = this.state;
    const isEditingField = !!prevFieldName;
    return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 7, lg: 6 }, /* @__PURE__ */ React.createElement("h2", { className: commonStyles.title }, isEditingField ? `Custom Field "${fieldName}"` : "New Custom Field"), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "field-name",
        name: "name",
        label: "Name",
        type: "text",
        value: fieldName,
        onChange: this.handleFieldNameChange,
        bsStyle: validation.errors.fieldName ? "error" : null,
        help: validation.errors.fieldName || "Name for this Field.",
        required: true
      }
    ), /* @__PURE__ */ React.createElement(FormGroup, { validationState: validation.errors.key_position ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Use Field as Event Key\u2003", /* @__PURE__ */ React.createElement(HoverForHelp, { title: "More about Event Keys", trigger: ["click", "hover"], placement: "right" }, /* @__PURE__ */ React.createElement(EventKeyHelpPopover, null))), /* @__PURE__ */ React.createElement(InputGroup, null, /* @__PURE__ */ React.createElement(InputGroup.Addon, null, /* @__PURE__ */ React.createElement(
      "input",
      {
        id: "is-key",
        name: "is-key",
        type: "checkbox",
        "aria-label": "Use Field as Event Key",
        onChange: this.toggleKey,
        checked: isKey
      }
    )), /* @__PURE__ */ React.createElement(
      FormControl,
      {
        id: "field-key",
        name: "key",
        type: "number",
        value: keyPosition,
        onChange: this.handleKeySortChange,
        disabled: !isKey
      }
    )), /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.key_position || "Indicates if this Field should be a Key and its order.")), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Field Data Type"), /* @__PURE__ */ React.createElement(FormControl.Static, null, "String")), /* @__PURE__ */ React.createElement(
      FormGroup,
      {
        controlId: "event-field-provider",
        validationState: validation.errors["config.providers[0].type"] ? "error" : null
      },
      /* @__PURE__ */ React.createElement(ControlLabel, null, "Set Value From"),
      /* @__PURE__ */ React.createElement(
        Select,
        {
          name: "event-field-provider",
          ignoreAccents: false,
          placeholder: "Select Value Source",
          onChange: this.handleProviderTypeChange,
          options: formatFieldValueProviders(),
          value: getConfigProviderType(config, ""),
          required: true
        }
      ),
      /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors["config.providers[0].type"] || "Select a source for the value of this Field.")
    )), /* @__PURE__ */ React.createElement(Col, { md: 12 }, this.renderFieldValueProviderForm()), /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: this.handleSubmit }, isEditingField ? "Update custom field" : "Add custom field"), /* @__PURE__ */ React.createElement(Button, { onClick: this.handleCancel }, "Cancel"))));
  }
}
FieldForm.defaultProps = {
  fieldName: "",
  config: {}
};
export default withTelemetry(FieldForm, "event-definition-fields");
